import { createClient, type SupabaseClient } from '@supabase/supabase-js'
import { env } from '@/config/env.ts'
import type {
  DatabaseClient,
  DatabaseResponse,
  DatabaseError,
  QueryFilter,
  QueryOptions,
  RealtimePayload,
} from './database.interface.ts'

let supabaseInstance: SupabaseClient | null = null

export function getSupabaseClient(): SupabaseClient {
  if (!supabaseInstance) {
    supabaseInstance = createClient(env.supabaseUrl, env.supabaseAnonKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
      },
    })
  }
  return supabaseInstance
}

function toError(err: unknown): DatabaseError {
  if (err && typeof err === 'object' && 'message' in err) {
    const e = err as { message: string; code?: string; details?: string }
    return { message: e.message, code: e.code, details: e.details }
  }
  return { message: String(err) }
}

interface QueryWithFilters {
  eq: (column: string, value: unknown) => QueryWithFilters
  neq: (column: string, value: unknown) => QueryWithFilters
  gt: (column: string, value: unknown) => QueryWithFilters
  gte: (column: string, value: unknown) => QueryWithFilters
  lt: (column: string, value: unknown) => QueryWithFilters
  lte: (column: string, value: unknown) => QueryWithFilters
  like: (column: string, value: string) => QueryWithFilters
  ilike: (column: string, value: string) => QueryWithFilters
  in: (column: string, value: unknown[]) => QueryWithFilters
  is: (column: string, value: null | boolean) => QueryWithFilters
}

function applyFilters<Q extends QueryWithFilters>(
  query: Q,
  filters?: QueryFilter[],
): Q {
  if (!filters) return query
  let q: Q = query
  for (const f of filters) {
    switch (f.operator) {
      case 'eq':
        q = q.eq(f.column, f.value) as Q
        break
      case 'neq':
        q = q.neq(f.column, f.value) as Q
        break
      case 'gt':
        q = q.gt(f.column, f.value) as Q
        break
      case 'gte':
        q = q.gte(f.column, f.value) as Q
        break
      case 'lt':
        q = q.lt(f.column, f.value) as Q
        break
      case 'lte':
        q = q.lte(f.column, f.value) as Q
        break
      case 'like':
        q = q.like(f.column, f.value as string) as Q
        break
      case 'ilike':
        q = q.ilike(f.column, f.value as string) as Q
        break
      case 'in':
        q = q.in(f.column, f.value as unknown[]) as Q
        break
      case 'is':
        q = q.is(f.column, f.value as null | boolean) as Q
        break
    }
  }
  return q
}

export function createSupabaseDatabase(): DatabaseClient {
  const client = getSupabaseClient()

  return {
    async findMany<T>(
      table: string,
      options?: QueryOptions,
    ): Promise<DatabaseResponse<T[]>> {
      try {
        let query = client.from(table).select(options?.select ?? '*')
        query = applyFilters(query, options?.filters)

        if (options?.orderBy) {
          query = query.order(options.orderBy.column, {
            ascending: options.orderBy.ascending ?? true,
          })
        }
        if (options?.limit) {
          query = query.limit(options.limit)
        }
        if (options?.offset !== undefined && options?.limit) {
          query = query.range(
            options.offset,
            options.offset + options.limit - 1,
          )
        }

        const { data, error } = await query
        if (error) return { data: null, error: toError(error) }
        return { data: data as T[], error: null }
      } catch (err) {
        return { data: null, error: toError(err) }
      }
    },

    async findOne<T>(
      table: string,
      options?: QueryOptions,
    ): Promise<DatabaseResponse<T>> {
      try {
        let query = client.from(table).select(options?.select ?? '*')
        query = applyFilters(query, options?.filters)
        const single = query.limit(1).maybeSingle()

        const { data, error } = await single
        if (error) return { data: null, error: toError(error) }
        return { data: data as T | null, error: null }
      } catch (err) {
        return { data: null, error: toError(err) }
      }
    },

    async count(
      table: string,
      filters?: QueryFilter[],
    ): Promise<DatabaseResponse<number>> {
      try {
        let query = client
          .from(table)
          .select('*', { count: 'exact', head: true })
        query = applyFilters(query, filters)
        const { count, error } = await query
        if (error) return { data: null, error: toError(error) }
        return { data: count ?? 0, error: null }
      } catch (err) {
        return { data: null, error: toError(err) }
      }
    },

    async findById<T>(
      table: string,
      id: string | number,
      select?: string,
    ): Promise<DatabaseResponse<T>> {
      try {
        const { data, error } = await client
          .from(table)
          .select(select ?? '*')
          .eq('id', id)
          .single()

        if (error) return { data: null, error: toError(error) }
        return { data: data as T, error: null }
      } catch (err) {
        return { data: null, error: toError(err) }
      }
    },

    async insert<T>(
      table: string,
      data: Partial<T> | Partial<T>[],
    ): Promise<DatabaseResponse<T[]>> {
      try {
        const rows = Array.isArray(data) ? data : [data]
        const { data: result, error } = await client
          .from(table)
          .insert(rows as Record<string, unknown>[])
          .select()

        if (error) return { data: null, error: toError(error) }
        return { data: result as T[], error: null }
      } catch (err) {
        return { data: null, error: toError(err) }
      }
    },

    async update<T>(
      table: string,
      data: Partial<T>,
      filters: QueryFilter[],
    ): Promise<DatabaseResponse<T[]>> {
      try {
        let query = client.from(table).update(data as Record<string, unknown>)

        query = applyFilters(query, filters)

        const { data: result, error } = await query.select()
        if (error) return { data: null, error: toError(error) }
        return { data: result as T[], error: null }
      } catch (err) {
        return { data: null, error: toError(err) }
      }
    },

    async updateById<T>(
      table: string,
      id: string | number,
      data: Partial<T>,
    ): Promise<DatabaseResponse<T>> {
      try {
        const { data: result, error } = await client
          .from(table)
          .update(data as Record<string, unknown>)
          .eq('id', id)
          .select()
          .single()

        if (error) return { data: null, error: toError(error) }
        return { data: result as T, error: null }
      } catch (err) {
        return { data: null, error: toError(err) }
      }
    },

    async upsert<T>(
      table: string,
      data: Partial<T> | Partial<T>[],
      onConflict?: string,
    ): Promise<DatabaseResponse<T[]>> {
      try {
        const rows = Array.isArray(data) ? data : [data]
        const { data: result, error } = await client
          .from(table)
          .upsert(
            rows as Record<string, unknown>[],
            onConflict ? { onConflict } : undefined,
          )
          .select()

        if (error) return { data: null, error: toError(error) }
        return { data: result as T[], error: null }
      } catch (err) {
        return { data: null, error: toError(err) }
      }
    },

    async remove<T>(
      table: string,
      filters: QueryFilter[],
    ): Promise<DatabaseResponse<T[]>> {
      try {
        let query = client.from(table).delete()
        query = applyFilters(query, filters)

        const { data: result, error } = await query.select()
        if (error) return { data: null, error: toError(error) }
        return { data: result as T[], error: null }
      } catch (err) {
        return { data: null, error: toError(err) }
      }
    },

    async removeById<T>(
      table: string,
      id: string | number,
    ): Promise<DatabaseResponse<T>> {
      try {
        const { data: result, error } = await client
          .from(table)
          .delete()
          .eq('id', id)
          .select()
          .single()

        if (error) return { data: null, error: toError(error) }
        return { data: result as T, error: null }
      } catch (err) {
        return { data: null, error: toError(err) }
      }
    },

    async rpc<T>(
      functionName: string,
      params?: Record<string, unknown>,
    ): Promise<DatabaseResponse<T>> {
      try {
        const { data, error } = await client.rpc(functionName, params)
        if (error) return { data: null, error: toError(error) }
        return { data: data as T, error: null }
      } catch (err) {
        return { data: null, error: toError(err) }
      }
    },

    subscribe(
      table: string,
      event: 'INSERT' | 'UPDATE' | 'DELETE' | '*',
      callback: (payload: RealtimePayload) => void,
    ): () => void {
      const channel = client
        .channel(`realtime-${table}`)
        .on(
          'postgres_changes',
          { event, schema: 'public', table },
          (payload) => {
            callback({
              eventType: payload.eventType as 'INSERT' | 'UPDATE' | 'DELETE',
              table: payload.table,
              new: (payload.new ?? {}) as Record<string, unknown>,
              old: (payload.old ?? {}) as Record<string, unknown>,
            })
          },
        )
        .subscribe()

      return () => {
        client.removeChannel(channel)
      }
    },
  }
}
